import { useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Hands-free / VAD loop for Conversation Mode.
 *
 * - Holds the mic open and uses Web Audio analyser RMS to detect speech vs
 *   silence. Each utterance is stopped after SILENCE_HOLD_MS of quiet.
 * - `onUtterance(blob)` is called with the recorded audio for each utterance
 *   longer than MIN_UTTERANCE_MS. The hook re-arms automatically while on.
 *
 * Returned API:
 *   handsFree: boolean
 *   hfStatus:  "idle" | "listening" | "speaking" | "processing"
 *   hfLevel:   0..1 visualizer
 *   toggle(next: boolean)
 *   markProcessing() / markListening()   ← called by parent around its async work
 */
const SPEECH_RMS = 0.025;
const SPEECH_HOLD_MS = 200;
const SILENCE_HOLD_MS = 800;
const MIN_UTTERANCE_MS = 350;

export function useVADHandsFree({ onUtterance, disabled }) {
  const [handsFree, setHandsFree] = useState(false);
  const [hfStatus, setHfStatus] = useState("idle");
  const [hfLevel, setHfLevel] = useState(0);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const vadStateRef = useRef({ mode: "idle", speechStart: 0, silenceStart: 0 });
  const vadRafRef = useRef(null);
  const handsFreeOnRef = useRef(false);

  const stop = () => {
    handsFreeOnRef.current = false;
    setHandsFree(false);
    setHfStatus("idle");
    setHfLevel(0);
    if (vadRafRef.current) {
      cancelAnimationFrame(vadRafRef.current);
      vadRafRef.current = null;
    }
    try {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.onstop = null;
        recorderRef.current.stop();
      }
    } catch { /* ignore */ }
    recorderRef.current = null;
    chunksRef.current = [];
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* ignore */ }
      audioCtxRef.current = null;
      analyserRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    vadStateRef.current = { mode: "idle", speechStart: 0, silenceStart: 0 };
  };

  const start = async () => {
    if (disabled) {
      toast.error("Wait until the current turn is finished");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Microphone not supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      const AudioCtor = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtor();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.fftSize);

      handsFreeOnRef.current = true;
      setHandsFree(true);
      setHfStatus("listening");

      const armRecorder = () => {
        if (!handsFreeOnRef.current) return;
        const mimeOptions = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
        const mimeType = mimeOptions.find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";
        const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        chunksRef.current = [];
        rec.ondataavailable = (e) => e.data?.size && chunksRef.current.push(e.data);
        rec.onstop = async () => {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
          chunksRef.current = [];
          recorderRef.current = null;
          if (!handsFreeOnRef.current) return;
          const startedAt = vadStateRef.current.speechStart || 0;
          const dur = startedAt ? performance.now() - startedAt : 0;
          if (blob.size === 0 || dur < MIN_UTTERANCE_MS) {
            armRecorder();
            return;
          }
          setHfStatus("processing");
          try {
            await onUtterance(blob);
          } finally {
            if (handsFreeOnRef.current) {
              setHfStatus("listening");
              armRecorder();
            }
          }
        };
        rec.start();
        recorderRef.current = rec;
        vadStateRef.current = { mode: "idle", speechStart: 0, silenceStart: 0 };
      };

      armRecorder();

      const tick = () => {
        if (!handsFreeOnRef.current) return;
        const a = analyserRef.current;
        if (!a) return;
        a.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setHfLevel(Math.min(1, rms * 4));

        const now = performance.now();
        const s = vadStateRef.current;
        const isSpeech = rms > SPEECH_RMS;

        if (s.mode === "idle") {
          if (isSpeech) {
            if (!s.speechStart) s.speechStart = now;
            if (now - s.speechStart > SPEECH_HOLD_MS) {
              s.mode = "speaking";
              s.silenceStart = 0;
              if (handsFreeOnRef.current) setHfStatus("speaking");
            }
          } else {
            s.speechStart = 0;
          }
        } else if (s.mode === "speaking") {
          if (isSpeech) {
            s.silenceStart = 0;
          } else {
            if (!s.silenceStart) s.silenceStart = now;
            if (now - s.silenceStart > SILENCE_HOLD_MS) {
              s.mode = "ending";
              try {
                if (recorderRef.current && recorderRef.current.state === "recording") {
                  recorderRef.current.stop();
                }
              } catch { /* ignore */ }
            }
          }
        }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      vadRafRef.current = requestAnimationFrame(tick);
    } catch {
      toast.error("Microphone permission denied");
      stop();
    }
  };

  const toggle = (next) => {
    if (next) start();
    else stop();
  };

  return { handsFree, hfStatus, hfLevel, toggle, stop };
}
